import { useEffect, useState } from 'react';

/**
 * Shared "has this browser captured an email yet?" flag.
 *
 * Tranche 2 stores a single localStorage marker the moment a soft email
 * capture succeeds (via the board's bottom sheet OR the persistent
 * "Save your boards" affordance). Both the board trigger
 * (`useEmailCaptureTrigger`) and the Layer-2 persistent affordance
 * (HeaderAccountMenu / You tab) read it so, once captured, neither prompts
 * again. Keeping the key + read/write in ONE module means the two surfaces
 * can never drift on what "already captured" means.
 *
 * A same-page capture dispatches `EMAIL_CAPTURED_EVENT` so any mounted
 * `useEmailCaptured()` consumer (the avatar dot, the dropdown row) clears
 * live without a reload; the native `storage` event covers other tabs.
 */

export const EMAIL_CAPTURED_KEY = 'thea:emailCaptured';
const EMAIL_CAPTURED_EVENT = 'thea:emailCapturedChange';

/** Synchronously read the captured flag (localStorage-guarded). */
export function isEmailCaptured(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(EMAIL_CAPTURED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Record a successful capture + notify same-page consumers. */
export function markEmailCaptured(): void {
  try {
    window.localStorage.setItem(EMAIL_CAPTURED_KEY, '1');
  } catch {
    /* private mode / quota — degrade gracefully */
  }
  try {
    window.dispatchEvent(new Event(EMAIL_CAPTURED_EVENT));
  } catch {
    /* SSR / no window — nothing listening anyway */
  }
}

/** Reactive read: re-renders when a capture happens in this or another tab. */
export function useEmailCaptured(): boolean {
  const [captured, setCaptured] = useState<boolean>(isEmailCaptured);
  useEffect(() => {
    const update = () => setCaptured(isEmailCaptured());
    window.addEventListener(EMAIL_CAPTURED_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(EMAIL_CAPTURED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return captured;
}
